import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "label-confirmation-input" }

import { computed, inject, ref, watch } from 'vue';
import { useStore } from 'vuex';
import type { UILang } from 'types/api/settings/invoicing/invoicingSettingsTypes';
import type { ConditionTranslation } from 'types/api/settings/invoicing/invoicingConditionsTypes';
import type { FileItem } from 'types/api/fileTypes';
import type { Translator } from 'types/injects/translator.type';
import { searchFiles as searchFilesApi } from 'api/file/fileService';
import { captureException } from '@sentry/vue';
import { useFile } from 'composables/useFile';
import { debounce } from 'lodash';

interface Props {
    uiLangs: UILang[];
    hadConditionFile: boolean;
}

interface AutoCompleteOption {
    label: string;
    value: number;
}

//le model conditions aura toujours au moins un item venant de l'api, car il se base sur les langues de l'interface

export default /*@__PURE__*/_defineComponent({
  __name: 'LabelConfirmationInput',
  props: /*@__PURE__*/_mergeModels({
    uiLangs: {},
    hadConditionFile: { type: Boolean }
  }, {
    "modelValue": {
    required: true,
},
    "modelModifiers": {},
    "showConditions": { type: Boolean, ...{ default: false } },
    "showConditionsModifiers": {},
  }),
  emits: ["update:modelValue", "update:showConditions"],
  setup(__props: any, { expose: __expose }) {



const conditions = _useModel<ConditionTranslation[]>(__props, "modelValue");
const showConditions = _useModel<boolean>(__props, 'showConditions');

const $t = inject('$t') as Translator;
const store = useStore();

const currentTab = ref(conditions.value.at(0)!.lang);
const fileSearchResults = ref<AutoCompleteOption[]>([]);
const fileSearchLoading = ref(false);
const labelError = ref<string>('');

const defaultUiLang = computed<string>(() => store.getters['corp/getUILang']);
const { getFileName, getFileSize } = useFile();

const getAutocompleteDropdownClasses = (langId: string): string => {
    return currentTab.value !== langId
        ? 'label-confirmation-input__file-search-input-dropdown--hidden'
        : '';
};

const getConditionLabel = (langId: string): string => {
    const condition = conditions.value.find((conditionItem) => conditionItem.lang === langId);
    return condition?.label ?? '';
};

const getDefaultFileValue = (langId: string): AutoCompleteOption[] => {
    const condition = conditions.value.find((conditionItem) => conditionItem.lang === langId);
    if (!condition?.file) {
        return [];
    }
    return [{ label: `${condition.file.name}.pdf`, value: condition.file.id }];
};

const searchFiles = debounce(async (term: string): Promise<void> => {
    fileSearchLoading.value = true;
    try {
        //on filtre par "attachement" et non "attachment" car c'est ce qui est attendu par l'api et que le changer serait un breaking change
        const { data: results } = (await searchFilesApi(term, ['attachement'], 'pdf')) as {
            data: FileItem[];
        };
        fileSearchResults.value = results.map((file: FileItem) => ({
            label: getFileName(file),
            value: file.id,
            caption: getFileSize(file.size),
        }));
    } catch (e) {
        captureException(e);
        fileSearchResults.value = [];
    } finally {
        fileSearchLoading.value = false;
    }
}, 200);

const updateConditionLabel = (langId: string, value: string): void => {
    const condition = conditions.value.find((condition) => condition.lang === langId);
    if (condition) {
        condition.label = value;
    }
    if (langId === defaultUiLang.value && value.trim() !== '') {
        labelError.value = '';
    }
};

const updateConditionFile = (langId: string, selected: AutoCompleteOption): void => {
    const condition = conditions.value.find((condition) => condition.lang === langId);
    if (condition) {
        condition.file = selected ? { id: selected.value, name: selected.label } : null;
    }
};

const validate = (): boolean => {
    if (!showConditions.value) {
        labelError.value = '';
        return true;
    }
    const defaultCondition = conditions.value.find(
        (condition) => condition.lang === defaultUiLang.value,
    );
    if (!defaultCondition || defaultCondition.label.trim() === '') {
        labelError.value = $t.__(
            'settings.sellsymodule.invoicing.conditions.confirmation_label_required',
        );
        currentTab.value = defaultUiLang.value;
        return false;
    }
    labelError.value = '';
    return true;
};

watch(showConditions, (value) => {
    if (!value) {
        labelError.value = '';
    }
});

__expose({ validate });

return (_ctx: any,_cache: any) => {
  const _component_CompassCheckbox = _resolveComponent("CompassCheckbox")!
  const _component_CompassInput = _resolveComponent("CompassInput")!
  const _component_CompassFormItem = _resolveComponent("CompassFormItem")!
  const _component_CompassAutoComplete = _resolveComponent("CompassAutoComplete")!
  const _component_CompassTabPane = _resolveComponent("CompassTabPane")!
  const _component_CompassTabs = _resolveComponent("CompassTabs")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_component_CompassCheckbox, {
      modelValue: showConditions.value,
      checked: showConditions.value,
      label: _unref($t).__('settings.sellsymodule.invoicing.conditions.confirmation_checkbox_label'),
      onChange: _cache[0] || (_cache[0] = ($event: any) => (showConditions.value = $event))
    }, null, 8 /* PROPS */, ["modelValue", "checked", "label"]),
    _createVNode(_Transition, {
      name: "slide",
      persisted: ""
    }, {
      default: _withCtx(() => [
        _withDirectives(_createVNode(_component_CompassTabs, {
          modelValue: currentTab.value,
          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((currentTab).value = $event))
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.uiLangs, (lang) => {
              return (_openBlock(), _createBlock(_component_CompassTabPane, {
                key: lang.id,
                label: lang.label,
                name: lang.id
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_CompassFormItem, {
                    label: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.confirmation_label_form_item_label',
                            )
                        ,
                    info: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.confirmation_label_caption',
                            )
                        ,
                    error: lang.id === defaultUiLang.value ? labelError.value : '',
                    required: lang.id === defaultUiLang.value,
                    class: "label-confirmation-input__text-input"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_CompassInput, {
                        modelValue: getConditionLabel(lang.id),
                        placeholder: 
                                _unref($t).__(
                                    'settings.sellsymodule.invoicing.conditions.confirmation_label_placeholder',
                                )
                            ,
                        error: lang.id === defaultUiLang.value && !!labelError.value,
                        "onUpdate:modelValue": ($event: any) => (updateConditionLabel(lang.id, $event))
                      }, null, 8 /* PROPS */, ["modelValue", "placeholder", "error", "onUpdate:modelValue"])
                    ]),
                    _: 2 /* DYNAMIC */
                  }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "info", "error", "required"]),
                  (__props.hadConditionFile)
                    ? (_openBlock(), _createBlock(_component_CompassFormItem, {
                        key: 0,
                        label: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.search_file_input_label',
                            )
                        ,
                        info: 
                            _unref($t).__(
                                'settings.sellsymodule.invoicing.conditions.search_file_input_caption',
                            )
                        ,
                        class: "label-confirmation-input__file-search-input"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_CompassAutoComplete, {
                            results: fileSearchResults.value,
                            loading: fileSearchLoading.value,
                            placeholder: 
                                _unref($t).__(
                                    'settings.sellsymodule.invoicing.conditions.search_file_input_placeholder',
                                )
                            ,
                            noResultsLabel: _unref($t).__('common.labels.no_result'),
                            defaultValues: getDefaultFileValue(lang.id),
                            menuClass: getAutocompleteDropdownClasses(lang.id),
                            onUpdateSearch: _cache[1] || (_cache[1] = ($event: any) => (_unref(searchFiles)($event))),
                            "onUpdate:modelValue": ($event: any) => (updateConditionFile(lang.id, $event))
                          }, null, 8 /* PROPS */, ["results", "loading", "placeholder", "noResultsLabel", "defaultValues", "menuClass", "onUpdate:modelValue"])
                        ]),
                        _: 2 /* DYNAMIC */
                      }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "info"]))
                    : _createCommentVNode("v-if", true)
                ]),
                _: 2 /* DYNAMIC */
              }, 1032 /* PROPS, DYNAMIC_SLOTS */, ["label", "name"]))
            }), 128 /* KEYED_FRAGMENT */))
          ]),
          _: 1 /* STABLE */
        }, 8 /* PROPS */, ["modelValue"]), [
          [_vShow, showConditions.value]
        ])
      ]),
      _: 1 /* STABLE */
    })
  ]))
}
}

})